import React, { useState } from 'react';
import { Form, Radio, Input, Button, Space, Row, Popover, Select } from 'antd';
import { connect } from 'dva';
import { routerRedux } from 'dva/router';
import { Typography } from '@hulu/react-style-components';
import envConfig from '@hulu/env-config';

import { CreateAdOverrideRuleWrapper, InfoCircleOutlinedWrapper } from './styles';
import {
  ALLOWED_APPS,
  AD_INVESTIGATOR,
  DEVICE,
  OVERRIDES,
  USER_PROFILE,
  VIDEO_AD_TYPE,
  PAUSE_AD_TYPE,
  SWID,
  FUBO_USER,
  VIEWER_ID,
} from '../../../constants';
import { expireAtFillout, formList, NARROW_FILLOUT, pauseAdIdFillout } from '../fillouts';
import LookupUserProfileIdModal from './LookupUserProfileIdModal';
import {
  ADINVESTIGATOR_CREATE_RULES_DEVICE,
  ADINVESTIGATOR_CREATE_RULES_FORM,
  ADINVESTIGATOR_CREATE_RULES_SUBMIT,
  ADINVESTIGATOR_CREATE_RULES_SWID,
  ADINVESTIGATOR_CREATE_RULES_TOGGLE,
  ADINVESTIGATOR_CREATE_RULES_ADTYPE_TOGGLE,
  ADINVESTIGATOR_CREATE_RULES_USERPROFILE,
  ADINVESTIGATOR_CREATE_RULES_ACCOUNTPROFILE,
  ADINVESTIGATOR_CREATE_RULES_FUBO_USER,
  ADINVESTIGATOR_CREATE_RULES_VIEWER,
  ADINVESTIGATOR_CREATE_RULES_APP,
} from '../../../testUtils';
import { FORM_NO_COLON_RULE, FORM_NOT_EMPTY_RULE, NO_EXTRA_SPACE_RULE } from '../../../utils';

const CreateAdOverrideRule = ({ dispatch, setTab, setViewOverrideBy, searchParams = {} }) => {
  const isAppSelectionEnabled =
    String(envConfig.REACT_APP_AD_INVESTIGATOR_APPS_SELECTION_ENABLED).toLowerCase() === 'true';
  const appOptions = Object.entries(ALLOWED_APPS).map(([value, label]) => ({ value, label }));

  const [form] = Form.useForm();
  const [overrideBy, setOverrideBy] = useState(searchParams?.createType ?? USER_PROFILE);
  const [adType, setAdType] = useState(searchParams?.adType ?? VIDEO_AD_TYPE);
  const [userProfileVisible, setUserProfileVisible] = useState(false);

  // Effects
  const handleUserProfileOrDeviceToggle = ({ target }) => {
    dispatch({
      type: 'app/setSearchParams',
      payload: { ...searchParams, createType: target.value },
    });
    setOverrideBy(target.value);
    if (![DEVICE, SWID].includes(target.value)) {
      form.setFieldsValue({ apps: [] });
    }
  };

  const handleAdTypeToggle = ({ target }) => {
    dispatch({
      type: 'app/setSearchParams',
      payload: { ...searchParams, adType: target.value },
    });

    setAdType(target.value);

    // SWID is only applicable for Video Ad Type. Default to User Profile.
    if (
      target.value === PAUSE_AD_TYPE &&
      (overrideBy === SWID || overrideBy === FUBO_USER || overrideBy === VIEWER_ID)
    ) {
      form.setFieldsValue({ overrideBy: USER_PROFILE });
      handleUserProfileOrDeviceToggle({ target: { value: USER_PROFILE } });
    }
  };

  const onFinish = (values) => {
    dispatch({ type: 'adInvestigator/submitRule', payload: { ...values, overrideBy, adType } });
    dispatch(routerRedux.push('/' + AD_INVESTIGATOR + '/' + OVERRIDES));
    setTab(OVERRIDES);
    setViewOverrideBy(overrideBy);
    setAdType(adType);
    dispatch({
      type: 'app/setSearchParams',
      payload: { ...searchParams, viewType: overrideBy },
    });
    form.resetFields();
  };

  const IdsInformation = (
    <div>
      <p>
        UserId: User identifier (a number) for Hulu (Vod , Live) flows. Example: 4562734.
        <br />
        AccountId: User identifier (UUID value) for D+ flows or Hulu(Vod, Live) unified platform flows.
        <br />
        ProfileId: It's an UUID if tied to an account otherwise it's a number tied to an user.
      </p>
    </div>
  );

  // Components
  const userProfileFillout = (
    <div data-testid={ADINVESTIGATOR_CREATE_RULES_USERPROFILE}>
      <Form.Item
        name="userId"
        label="User/Account Id"
        rules={[
          { required: true, message: 'Please input user/account id value' },
          FORM_NOT_EMPTY_RULE,
          FORM_NO_COLON_RULE,
        ]}
        validateTrigger="onSubmit"
      >
        <Input placeholder="User/Account ID" />
      </Form.Item>
      <Form.Item
        name="profileId"
        label="Profile Id"
        rules={[{ required: true, message: 'Please input profile id value' }, FORM_NOT_EMPTY_RULE, FORM_NO_COLON_RULE]}
        validateTrigger="onSubmit"
      >
        <Input placeholder="Profile ID" />
      </Form.Item>
      <Row justify="center">
        <Button type="primary" onClick={() => setUserProfileVisible(true)}>
          Don't know IDs? Lookup using sign-in
        </Button>
        <Popover content={IdsInformation} title="Ids Information" trigger="hover">
          <InfoCircleOutlinedWrapper />
        </Popover>
      </Row>
      <br />
      <LookupUserProfileIdModal open={userProfileVisible} setOpen={setUserProfileVisible} />
    </div>
  );

  const accountProfileFillout = (
    <div data-testid={ADINVESTIGATOR_CREATE_RULES_ACCOUNTPROFILE}>
      <Form.Item
        name="userId"
        label="User/Account Id"
        rules={[
          { required: true, message: 'Please input user/account id value' },
          FORM_NOT_EMPTY_RULE,
          FORM_NO_COLON_RULE,
        ]}
        validateTrigger="onSubmit"
      >
        <Input placeholder="User/Account ID" />
      </Form.Item>
      <Form.Item
        name="profileId"
        label="Profile Id"
        rules={[{ required: true, message: 'Please input profile id value' }, FORM_NOT_EMPTY_RULE, FORM_NO_COLON_RULE]}
        validateTrigger="onSubmit"
      >
        <Input placeholder="Profile ID" />
      </Form.Item>
    </div>
  );

  const deviceFillout = (
    <div data-testid={ADINVESTIGATOR_CREATE_RULES_DEVICE}>
      <Form.Item
        name="deviceId"
        label="Device Id"
        rules={[
          { required: true, message: 'Please input device id value' },
          FORM_NOT_EMPTY_RULE,
          FORM_NO_COLON_RULE,
          NO_EXTRA_SPACE_RULE,
        ]}
      >
        <Input placeholder="Device ID" />
      </Form.Item>
    </div>
  );

  const swidFillout = (
    <div data-testid={ADINVESTIGATOR_CREATE_RULES_SWID}>
      <Form.Item
        name="swid"
        label="SWID"
        rules={[
          { required: true, message: 'Please input SWID value' },
          FORM_NOT_EMPTY_RULE,
          FORM_NO_COLON_RULE,
          NO_EXTRA_SPACE_RULE,
        ]}
      >
        <Input placeholder="SWID" />
      </Form.Item>
    </div>
  );

  const fuboUserFillout = (
    <div data-testid={ADINVESTIGATOR_CREATE_RULES_FUBO_USER}>
      <Form.Item
        name="fuboUserId"
        label="Fubo User Id"
        rules={[
          { required: true, message: 'Please input Fubo user id value' },
          FORM_NOT_EMPTY_RULE,
          FORM_NO_COLON_RULE,
          NO_EXTRA_SPACE_RULE,
        ]}
      >
        <Input placeholder="Fubo user id" />
      </Form.Item>
    </div>
  );

  const viewerFillout = (
    <div data-testid={ADINVESTIGATOR_CREATE_RULES_VIEWER}>
      <Form.Item
        name="viewerId"
        label="MLB Viewer Id"
        rules={[
          { required: true, message: 'Please input MLB viewer id value' },
          FORM_NOT_EMPTY_RULE,
          FORM_NO_COLON_RULE,
          NO_EXTRA_SPACE_RULE,
        ]}
      >
        <Input placeholder="MLB viewer id" />
      </Form.Item>
    </div>
  );

  const overrideByToggle = (
    <Form.Item label="Override By" name="overrideBy">
      <div data-testid={ADINVESTIGATOR_CREATE_RULES_TOGGLE}>
        <Radio.Group
          defaultValue={overrideBy}
          value={form.getFieldValue('overrideBy')}
          onChange={handleUserProfileOrDeviceToggle}
        >
          <Radio.Button value={USER_PROFILE}>User/Account</Radio.Button>
          <Radio.Button value={DEVICE}>Device</Radio.Button>
          {adType === VIDEO_AD_TYPE ? (
            <>
              <Radio.Button value={SWID}>SWID</Radio.Button>
              <Radio.Button value={FUBO_USER}>Fubo User</Radio.Button>
              <Radio.Button value={VIEWER_ID}>MLB Viewer</Radio.Button>
            </>
          ) : null}
        </Radio.Group>
      </div>
    </Form.Item>
  );

  const shouldShowAppsSelector = isAppSelectionEnabled && [DEVICE, SWID].includes(overrideBy);
  const appSelector = shouldShowAppsSelector ? (
    <div data-testid={ADINVESTIGATOR_CREATE_RULES_APP}>
      <Form.Item name="apps" label="Apps">
        <Select mode="multiple" allowClear showSearch options={appOptions} placeholder="Optional: select apps" />
      </Form.Item>
    </div>
  ) : null;

  const adTypeToggle = (
    <Form.Item label="Ad Type" name="adType">
      <div data-testid={ADINVESTIGATOR_CREATE_RULES_ADTYPE_TOGGLE}>
        <Radio.Group value={adType} onChange={handleAdTypeToggle}>
          <Radio.Button value={VIDEO_AD_TYPE}>Video Ads</Radio.Button>
          <Radio.Button value={PAUSE_AD_TYPE}>Pause Ads</Radio.Button>
        </Radio.Group>
      </div>
    </Form.Item>
  );

  const resetSubmitButtons = (
    <Form.Item>
      <div data-testid={ADINVESTIGATOR_CREATE_RULES_SUBMIT}>
        <Space>
          <Button htmlType="button" onClick={() => form.resetFields()}>
            Reset
          </Button>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Space>
      </div>
    </Form.Item>
  );

  const renderOverrideByFillout = () => {
    if (overrideBy === USER_PROFILE) {
      return adType === VIDEO_AD_TYPE ? userProfileFillout : accountProfileFillout;
    }
    if (overrideBy === DEVICE) {
      return deviceFillout;
    }
    if (overrideBy === SWID) {
      return swidFillout;
    }
    if (overrideBy === FUBO_USER) {
      return fuboUserFillout;
    }
    return viewerFillout;
  };

  return (
    <CreateAdOverrideRuleWrapper>
      <div data-testid={ADINVESTIGATOR_CREATE_RULES_FORM}>
        <Typography variant="h3">Create Override Rule</Typography>
        <br />
        <Form form={form} onFinish={onFinish} initialValues={{ overrideBy }}>
          {adTypeToggle}
          {overrideByToggle}
          {appSelector}
          {renderOverrideByFillout()}
          {adType === VIDEO_AD_TYPE ? formList('overrideRules', NARROW_FILLOUT) : pauseAdIdFillout(NARROW_FILLOUT)}
          {expireAtFillout()}
          {resetSubmitButtons}
        </Form>
      </div>
    </CreateAdOverrideRuleWrapper>
  );
};

function mapStateToProps({ app: { searchParams } }) {
  return { searchParams };
}

export default connect(mapStateToProps)(CreateAdOverrideRule);
